import React from 'react';
import styled from 'styled-components';

import { AdminListPageConfig, AdminListPageRenderer, useAdminListPage } from 'hooks/useAdminListPage';
import { simpleDateAtTime } from 'lib/date';
import {
  adminBulkImportsQueryKeys,
  adminIndexBulkImports,
  BULK_IMPORT_TYPE_LABELS,
  BulkImport,
  BulkImportStatus,
  BulkImportType,
} from 'services/api/admin/bulkImports';
import { colors } from 'styles/theme/colors';

const STATUS_COLORS: Record<string, string> = {
  [BulkImportStatus.PREVIEWING]: colors.black25,
  [BulkImportStatus.PREVIEWED]: colors.primaryBlue,
  [BulkImportStatus.APPLYING]: colors.accentYellow,
  [BulkImportStatus.COMPLETED]: colors.accentGreen,
  [BulkImportStatus.ERRORED]: colors.accentRed,
  [BulkImportStatus.CANCELED]: colors.black25,
};

// Previewing and applying are transient states that last seconds — not useful
// filter targets.
const FILTERABLE_STATUSES = [
  BulkImportStatus.PREVIEWED,
  BulkImportStatus.COMPLETED,
  BulkImportStatus.ERRORED,
  BulkImportStatus.CANCELED,
];

const statusOptions = FILTERABLE_STATUSES.map((status) => ({
  id: status,
  name: status.charAt(0).toUpperCase() + status.slice(1),
}));

const typeOptions = Object.values(BulkImportType).map((type) => ({
  id: type,
  name: BULK_IMPORT_TYPE_LABELS[type],
}));

const config: AdminListPageConfig<BulkImport> = {
  title: 'Bulk Import',
  columnStorageKey: 'admin.bulkImport',
  queryKey: adminBulkImportsQueryKeys.index(),
  fetchFn: (params, signal) => adminIndexBulkImports(params, signal),
  fetchParams: { defaultSort: 'createdAt desc' },
  addButton: { label: 'New Import', path: '/admin/bulk-import/new', permission: 'adminBulkImportCreate' },
  filters: [
    {
      key: 'importType',
      label: 'Type',
      type: 'dropdown',
      staticOptions: typeOptions,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
    {
      key: 'status',
      label: 'Status',
      type: 'dropdown',
      staticOptions: statusOptions,
      getOptionLabel: (o) => o.name,
      getOptionValue: (o) => o.id,
    },
  ],
  columns: [
    {
      id: 'importType',
      header: 'Type',
      render: (row) => <span>{row.typeLabel}</span>,
    },
    { id: 'status', header: 'Status', render: (row) => <StatusBadge status={row.status} /> },
    {
      id: 'totalRows',
      header: 'Rows',
      sortable: false,
      render: (row) => row.summary.totalRows?.toLocaleString() ?? '—',
    },
    { id: 'createdAt', header: 'Created On', render: (row) => (row.createdAt ? simpleDateAtTime(row.createdAt) : '') },
  ],
  rowActions: [
    { label: 'View Import Details', onClick: (row, { navigate }) => navigate(`/admin/bulk-import/${row.id}`) },
  ],
};

function StatusBadge({ status }: { status: string }) {
  return (
    <Container>
      <Dot color={STATUS_COLORS[status] ?? colors.black25} />
      <span style={{ textTransform: 'capitalize' }}>{status}</span>
    </Container>
  );
}

const Container = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Dot = styled.div<{ color: string }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ color }) => color};
  flex-shrink: 0;
`;

export default function BulkImportHistory() {
  const state = useAdminListPage(config);
  return <AdminListPageRenderer config={config} state={state} />;
}
